/**
 * Pure event catalog — safe to import from client, server, or shared code.
 * No OpenPanel / browser imports.
 *
 * `name` is the OpenPanel event label (human-readable, Midday-style).
 * `channel` groups events in dashboards.
 */

export type TrackPayload = {
  event: string;
  channel: string;
} & Record<string, unknown>;

export const LogEvents = {
  // App
  AppBoot: { name: "App Boot", channel: "app" },

  // Auth & session
  SignIn: { name: "User Signed In", channel: "login" },
  SignOut: { name: "User Signed Out", channel: "login" },
  Registered: { name: "User Registered", channel: "registered" },
  UserRegistrationCompleted: {
    name: "User Registration Completed",
    channel: "auth",
  },
  UserRegistrationError: {
    name: "User Registration Error",
    channel: "auth",
  },

  // Business / team
  ChangeBusiness: { name: "Change Business", channel: "team" },
  ViewAsBusinessMember: {
    name: "View As Business Member",
    channel: "team",
  },
  MemberInvited: { name: "Member Invited", channel: "team" },
  MemberRemoved: { name: "Member Removed", channel: "team" },
  BusinessRegistrationStarted: {
    name: "Business Registration Started",
    channel: "onboarding",
  },
  BusinessRegistrationCompleted: {
    name: "Business Registration Completed",
    channel: "onboarding",
  },
  BusinessRegistrationError: {
    name: "Business Registration Error",
    channel: "onboarding",
  },

  // Onboarding
  OnboardingStarted: { name: "Onboarding Started", channel: "onboarding" },
  OnboardingStepViewed: {
    name: "Onboarding Step Viewed",
    channel: "onboarding",
  },
  OnboardingStepCompleted: {
    name: "Onboarding Step Completed",
    channel: "onboarding",
  },
  OnboardingStepSkipped: {
    name: "Onboarding Step Skipped",
    channel: "onboarding",
  },
  OnboardingCompleted: { name: "Onboarding Completed", channel: "onboarding" },
  OnboardingTeamCreated: {
    name: "Onboarding Team Created",
    channel: "onboarding",
  },
  ProductSelected: { name: "Product Selected", channel: "onboarding" },

  // Billing & subscription
  StripeCheckoutView: { name: "Stripe Checkout Viewed", channel: "billing" },
  StripeCheckoutInitiated: {
    name: "Stripe Checkout Initiated",
    channel: "billing",
  },
  StripeCheckoutError: {
    name: "Stripe Checkout Error",
    channel: "billing",
  },
  JoinStarted: { name: "Join Flow Started", channel: "billing" },
  JoinCheckoutInitiated: {
    name: "Join Checkout Initiated",
    channel: "billing",
  },
  JoinCheckoutError: { name: "Join Checkout Error", channel: "billing" },
  JoinCompleted: { name: "Join Completed", channel: "billing" },
  CheckoutStarted: { name: "Checkout Started", channel: "billing" },
  CheckoutCompleted: { name: "Checkout Completed", channel: "billing" },
  SubscriptionCanceled: {
    name: "Subscription Canceled",
    channel: "billing",
  },

  // Job ads & recruitment
  JobAdCreated: { name: "Job Ad Created", channel: "recruit" },
  JobAdPublished: { name: "Job Ad Published", channel: "recruit" },
  JobAdUpdated: { name: "Job Ad Updated", channel: "recruit" },
  JobAdDuplicated: { name: "Job Ad Duplicated", channel: "recruit" },
  JobAdArchived: { name: "Job Ad Archived", channel: "recruit" },
  JobAdDeleted: { name: "Job Ad Deleted", channel: "recruit" },
  JobAdPreviewOpened: {
    name: "Job Ad Preview Opened",
    channel: "recruit",
  },

  // Matches & pipeline
  MatchViewed: { name: "Match Viewed", channel: "matches" },
  MatchShortlisted: { name: "Match Shortlisted", channel: "matches" },
  MatchRejected: { name: "Match Rejected", channel: "matches" },
  MatchHired: { name: "Match Hired", channel: "matches" },
  MatchNotesSaved: { name: "Match Notes Saved", channel: "matches" },
  SendForReview: { name: "Send For Review", channel: "matches" },
  BulkMessageSent: { name: "Bulk Message Sent", channel: "matches" },

  // Candidates
  CandidateProfileOpened: {
    name: "Candidate Profile Opened",
    channel: "candidates",
  },
  CandidateExported: { name: "Candidate Exported", channel: "candidates" },
  ExternalCandidateLinkOpened: {
    name: "External Candidate Link Opened",
    channel: "candidates",
  },

  // Chat
  ChatOpened: { name: "Chat Opened", channel: "chat" },
  ChatMessageSent: { name: "Chat Message Sent", channel: "chat" },
  ChatThreadCreated: { name: "Chat Thread Created", channel: "chat" },

  // Leads
  LeadFormOpened: { name: "Lead Form Opened", channel: "leads" },
  LeadSubmitted: { name: "Lead Submitted", channel: "leads" },
  LeadStatusChanged: { name: "Lead Status Changed", channel: "leads" },

  // Assistant / AI
  AssistantOpened: { name: "Assistant Opened", channel: "assistant" },
  AssistantMessageSent: {
    name: "Assistant Message Sent",
    channel: "assistant",
  },
  AssistantNewChat: { name: "Assistant New Chat", channel: "assistant" },
  AssistantQuickAction: {
    name: "Assistant Quick Action",
    channel: "assistant",
  },
  AssistantSuggestionUsed: {
    name: "Assistant Suggestion Used",
    channel: "assistant",
  },
  AssistantStopped: { name: "Assistant Stopped", channel: "assistant" },

  // Intelligence (sales leadership AI under /intelligence)
  IntelligenceOpened: { name: "Intelligence Opened", channel: "intelligence" },
  IntelligenceNewChat: { name: "Intelligence New Chat", channel: "intelligence" },
  IntelligenceThreadSelected: {
    name: "Intelligence Thread Selected",
    channel: "intelligence",
  },
  IntelligenceMessageSent: {
    name: "Intelligence Message Sent",
    channel: "intelligence",
  },
  IntelligenceThreadDeleted: {
    name: "Intelligence Thread Deleted",
    channel: "intelligence",
  },
  IntelligenceFeedbackSubmitted: {
    name: "Intelligence Feedback Submitted",
    channel: "intelligence",
  },

  // Pulse
  PulseOpened: {
    name: "Pulse Opened",
    channel: "intelligence",
  },
  PulseScoreSubmitted: {
    name: "Pulse Score Submitted",
    channel: "intelligence",
  },
  PulsePeriodCompleted: {
    name: "Pulse Period Completed",
    channel: "intelligence",
  },
  PulseHistoryViewed: {
    name: "Pulse History Viewed",
    channel: "intelligence",
  },

  // Integrations & connectors
  ConnectorModalOpened: {
    name: "Connector Modal Opened",
    channel: "connectors",
  },
  ConnectorConnected: { name: "Connector Connected", channel: "connectors" },
  ConnectorDisconnected: {
    name: "Connector Disconnected",
    channel: "connectors",
  },
  ConnectorViewed: { name: "Connector Viewed", channel: "connectors" },

  // Notifications
  NotificationBellOpened: {
    name: "Notification Bell Opened",
    channel: "notifications",
  },
  NotificationPreferencesUpdated: {
    name: "Notification Preferences Updated",
    channel: "notifications",
  },

  // Settings & account
  ThemeChanged: { name: "Theme Changed", channel: "settings" },
  LanguageChanged: { name: "Language Changed", channel: "settings" },
  ApiKeyCreated: { name: "API Key Created", channel: "settings" },
  ApiKeyDeleted: { name: "API Key Deleted", channel: "settings" },
  AccountDeleted: { name: "Account Deleted", channel: "settings" },

  // Support & feedback
  SupportTicket: { name: "Support Ticket", channel: "support" },
  SendFeedback: { name: "Send Feedback", channel: "feedback" },

  // Marketing / product surface
  CTA: { name: "CTA Clicked", channel: "website" },

  // AI — Job ad generation
  AiExpandIntroduction: {
    name: "AI Expand Introduction",
    channel: "ai",
  },
  AiAnalyzeDescription: {
    name: "AI Analyze Description",
    channel: "ai",
  },
  AiGenerateQualifyingQuestions: {
    name: "AI Generate Qualifying Questions",
    channel: "ai",
  },

  // Cross-cutting
  SearchOpened: { name: "Search Opened", channel: "general" },
  CopiedToClipboard: { name: "Copied to Clipboard", channel: "general" },
  ErrorBoundaryShown: { name: "Error Boundary Shown", channel: "general" },

  // Intelligence — Pulse nudges
  PulseNudgeShown: { name: "Pulse Nudge Shown", channel: "intelligence" },
  PulseNudgeCta: { name: "Pulse Nudge CTA", channel: "intelligence" },
  PulseNudgeDismiss: { name: "Pulse Nudge Dismissed", channel: "intelligence" },
  PulseConfigured: { name: "Pulse Configured", channel: "intelligence" },
  PulseConfigChangeScheduled: {
    name: "Pulse Config Change Scheduled",
    channel: "intelligence",
  },
  PulseConfigChangeApplied: {
    name: "Pulse Config Change Applied",
    channel: "intelligence",
  },
  PulseTeamOverrideSet: {
    name: "Pulse Team Override Set",
    channel: "intelligence",
  },

  // Pilot metrics (GRA-217): five signals Jonas asked to measure from day
  // one - active managers, time to first insight, share of accepted
  // insights, thumbs up/down feedback, 1:1s with an agenda. See
  // apps/graftus-webapp-2/src/analytics/EVENTS.md for the full catalog and
  // the dashboard recipe for each metric.
  InsightShown: { name: "Insight Shown", channel: "insights" },
  InsightShareDecided: { name: "Insight Share Decided", channel: "insights" },
  InsightDismissed: { name: "Insight Dismissed", channel: "insights" },
  InsightFeedback: { name: "Insight Feedback", channel: "insights" },
  ActionCreated: { name: "Action Created", channel: "actions" },
  ActionAccepted: { name: "Action Accepted", channel: "actions" },
  RoutineCompleted: { name: "Routine Completed", channel: "routines" },
  TalkBooked: { name: "Talk Booked", channel: "talks" },
  TalkCompleted: { name: "Talk Completed", channel: "talks" },
} as const;

export type LogEventDefinition = (typeof LogEvents)[keyof typeof LogEvents];
